import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import MarketOverview from '../components/MarketOverview';
import AIAnalysisModal from '../components/AIAnalysisModal';
import TradingViewMarketData from '../components/TradingViewMarketData';
import TradingViewETFHeatmap from '../components/TradingViewETFHeatmap';
import LoadingLogo from '../components/LoadingLogo';
import Disclaimer from '../components/Disclaimer';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Eye,
  Zap,
  Clock,
  Brain,
  Briefcase,
  ChevronDown,
  ChevronRight,
  X
} from 'lucide-react';
import apiService from '../services/apiService';
import InlineDisclaimer from '../components/InlineDisclaimer';
import { UsageBar } from '../components/UsageBar';
import { useSubscription } from '../contexts/SubscriptionContext';

// Escape HTML entities to prevent XSS before applying highlight markup
import DOMPurify from 'dompurify';

const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.appendChild(document.createTextNode(text));
  return div.innerHTML;
};

// Helper function to highlight important elements in Daily Briefing
const highlightDailyBriefing = (text) => {
  if (!text) return text;

  // Sanitize first to prevent XSS via dangerouslySetInnerHTML
  let result = escapeHtml(text);
  
  // Use Tailwind classes directly in HTML for better compatibility
  const orangeStyle = 'font-bold text-orange-600';
  
  // 1. Percentages with +/- signs - BOLD AND ORANGE
  result = result.replace(/([+-]\d+\.?\d*%)/g, 
    `<span class="${orangeStyle}" style="font-size: 1.1em;">$1</span>`);
  
  // 2. Company names
  const companies = ['Apple', 'Amazon', 'Microsoft', 'Tesla', 'NVIDIA', 'Nvidia', 'Meta', 'Google', 'AMD', 'Intel'];
  companies.forEach(company => {
    const regex = new RegExp(`\\b(${company})\\b`, 'gi');
    result = result.replace(regex, `<span class="${orangeStyle}">$1</span>`);
  });
  
  // 3. Crypto names
  const cryptos = ['Bitcoin', 'Ethereum', 'Solana', 'Cardano', 'Ripple', 'Dogecoin'];
  cryptos.forEach(crypto => {
    const regex = new RegExp(`\\b(${crypto})\\b`, 'gi');
    result = result.replace(regex, `<span class="${orangeStyle}">$1</span>`);
  });
  
  // 4. Crypto symbols
  const cryptoSymbols = ['BTC', 'ETH', 'XRP', 'SOL', 'ADA', 'DOGE'];
  cryptoSymbols.forEach(symbol => {
    const regex = new RegExp(`\\b(${symbol})\\b`, 'g');
    result = result.replace(regex, `<span class="${orangeStyle}">$1</span>`);
  });
  
  // 5. Commodities
  const commodities = ['Gold', 'Silver', 'Oil', 'Crude'];
  commodities.forEach(commodity => {
    const regex = new RegExp(`\\b(${commodity})\\b`, 'gi');
    result = result.replace(regex, `<span class="${orangeStyle}">$1</span>`);
  });
  
  // 6. VIX
  result = result.replace(/\bVIX\b/g, `<span class="${orangeStyle}">VIX</span>`);
  
  return result;
};

const HomePage = () => {
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const tier = subscription?.tier || 'free';
  const [dailyRecap, setDailyRecap] = useState(null);
  const [overallSentiment, setOverallSentiment] = useState(null);
  const [recapLoading, setRecapLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [usageExceeded, setUsageExceeded] = useState(false);
  const [welcomeExpanded, setWelcomeExpanded] = useState(() => {
    return !localStorage.getItem('era_welcome_dismissed');
  });
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Fetch overall sentiment from webhook data via apiService
  useEffect(() => {
    const fetchOverallSentiment = async () => {
      try {
        const data = await apiService.getOverallSentiment();
        setOverallSentiment(data);
        setDataLoaded(true);
      } catch (err) {
        console.error('Error fetching overall sentiment:', err);
      }
    };

    fetchOverallSentiment();
  }, []);

  // Fetch daily recap separately
  useEffect(() => {
    const fetchDailyRecap = async () => {
      try {
        setRecapLoading(true);
        
        // Set timeout for AI-generated recap (Claude can take 20-40 seconds)
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 45000)
        );
        
        const dataPromise = apiService.getDailyRecap();
        
        const recapData = await Promise.race([dataPromise, timeoutPromise]);
        setDailyRecap(recapData);
        // Update usage tracking
        try {
          const usage = await apiService.getUsageData('daily_briefing');
          if (usage) setUsageData(usage);
        } catch (e) { /* ignore usage fetch error */ }
      } catch (err) {
        console.error('Error fetching daily recap:', err);
        if (err.response?.status === 429) {
          setUsageExceeded(true);
          const detail = err.response?.data?.detail;
          setUsageData({ used: detail?.used || 4, limit: detail?.limit || 4 });
        }
        setDailyRecap(null);
      } finally {
        setRecapLoading(false);
      }
    };

    fetchDailyRecap();
  }, []);


  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold font-heading text-navy flex items-center space-x-2">
            <Zap className="w-8 h-8 text-[#F7931A]" />
            <span>Cockpit</span>
          </h1>
          <p className="text-gray-body mt-1">Your command center for cross-asset market intelligence</p>
        </div>
        {dataLoaded && (
          <Badge variant="secondary" className="bg-[#F7931A] bg-opacity-10 text-[#F7931A] border border-[#F7931A] border-opacity-30">
            <Activity className="w-3 h-3 mr-1" />
            Live Market Data
          </Badge>
        )}
      </div>

      {/* Welcome Guide - Your Era Journey */}
      <Card className="bg-gradient-to-br from-orange-50 via-yellow-50 to-orange-50 border border-orange-200 shadow-lg">
        <CardHeader className="cursor-pointer" onClick={() => {
          const next = !welcomeExpanded;
          setWelcomeExpanded(next);
          if (!next) localStorage.setItem('era_welcome_dismissed', 'true');
          else localStorage.removeItem('era_welcome_dismissed');
        }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl font-heading text-navy">Welcome to Your Era Journey</CardTitle>
                <p className="text-gray-600 text-sm mt-1">Master market intelligence in 2 minutes</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-gray-600 hover:text-gray-900"
            >
              {welcomeExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </Button>
          </div>
        </CardHeader>
        {welcomeExpanded && (
          <CardContent>
          <div className="space-y-4">
            {/* Introduction Video - Button to Open Modal */}
            <div className="bg-gradient-to-br from-orange-100 to-yellow-100 rounded-lg p-4 border border-orange-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Welcome to Era - Introduction Video</h4>
                    <p className="text-xs text-gray-600">2 minutes • Perfect for first-time users</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setIsVideoModalOpen(true)}
                  className="bg-bitcoin-orange hover:bg-orange-600 text-white"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Watch Video
                </Button>
              </div>
            </div>

            {/* Journey Steps */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Step 1: Cockpit */}
              <div className="bg-white rounded-lg p-4 border border-orange-200 hover:shadow-md transition-shadow cursor-pointer group">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-orange-600 font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">Start with your Daily Briefing</h3>
                    <p className="text-sm text-gray-600 mt-1">Get your 2-minute market snapshot with AI analysis</p>
                  </div>
                </div>
              </div>

              {/* Step 2: Assets */}
              <div 
                className="bg-white rounded-lg p-4 border border-orange-200 hover:shadow-md transition-shadow group cursor-pointer"
                onClick={() => {
                  window.dispatchEvent(new Event('toggleAssets'));
                }}
              >
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-orange-600 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">
                      Explore Assets
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">Click to toggle Assets section in sidebar</p>
                  </div>
                </div>
              </div>

              {/* Step 3: Investment Tools */}
              <div 
                className="bg-white rounded-lg p-4 border border-yellow-200 hover:shadow-md transition-shadow group relative overflow-hidden cursor-pointer"
                onClick={() => {
                  window.dispatchEvent(new Event('toggleInvestmentTools'));
                }}
              >
                <div className="absolute top-2 right-2">
                  <Badge className="bg-bitcoin-orange text-white text-xs px-2 py-0.5 rounded-full font-bold">PRO</Badge>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-600 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">
                      Investment Tools
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">Click to toggle Investment Tools in sidebar</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-orange-200">
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-orange-600 hover:bg-orange-50 border-orange-300"
                  onClick={() => navigate('/curiosity')}
                >
                  <Brain className="w-4 h-4 mr-2" />
                  Learn with Curiosity
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-orange-600 hover:bg-orange-50 border-orange-300"
                  onClick={() => navigate('/watchlist')}
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Build Your Watchlist
                  <Badge className="bg-bitcoin-orange text-white text-[10px] px-1.5 py-0 ml-2 rounded-full font-bold">PRO</Badge>
                </Button>
              </div>
              <p className="text-xs text-gray-500 italic">💡 Hover over sidebar items for helpful tooltips</p>
            </div>
          </div>
        </CardContent>
        )}
      </Card>

      {/* Daily Briefing + Market Overview - TOP SECTION WITH 2/3 and 1/3 SPLIT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Daily Briefing - 2/3 Width */}
        <div className="lg:col-span-2 h-full">
          <Card className="h-full flex flex-col bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-navy text-xl font-heading flex items-center space-x-2">
                  <Clock className="w-6 h-6 text-[#F7931A]" />
                  <span>Daily Briefing</span>
                </CardTitle>
                <Badge variant="secondary" className="bg-[#F7931A] text-white hover:bg-orange-600 text-xs">
                  <Brain className="w-3 h-3 mr-1" />
                  AI
                </Badge>
              </div>
            </CardHeader>
            {tier === 'free' && usageData && (
              <div className="px-6"><UsageBar feature="daily_briefing" used={usageData.used} limit={usageData.limit} /></div>
            )}
            <CardContent className="flex-1 space-y-4">
              {recapLoading ? (
                <div className="text-center py-8">
                  <LoadingLogo size="md" text="Analyzing cross-asset markets..." />
                </div>
              ) : dailyRecap ? (
                <>
                  {/* AI Summary - Enhanced with Highlighting */}
                  <div className="bg-white rounded-lg p-6 shadow-sm border border-orange-100">
                    <div 
                      className="text-gray-800 text-lg leading-relaxed font-medium"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(highlightDailyBriefing(dailyRecap.ai_summary || "Daily briefing is being prepared. Please check back shortly for your AI-generated market analysis.")) }}
                    />
                    {dailyRecap.market_data_as_of && (
                      <div className="mt-3 text-xs text-gray-400" data-testid="daily-briefing-asof">
                        Market data as of {new Date(dailyRecap.market_data_as_of).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </div>

                  {/* Quick Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {/* Market Sentiment - FROM OVERALL SENTIMENT WEBHOOK */}
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                      <h4 className="text-gray-500 text-sm font-medium mb-2">Overall Market Sentiment</h4>
                      <div className="flex items-center space-x-2">
                        {overallSentiment ? (
                          <>
                            {overallSentiment.score >= 60 ?
                              <TrendingUp className="w-5 h-5 text-green-600" /> :
                              overallSentiment.score >= 40 ?
                              <Activity className="w-5 h-5 text-yellow-600" /> :
                              <TrendingDown className="w-5 h-5 text-red-500" />
                            }
                            <div>
                              <div className="text-gray-900 font-semibold">
                                {overallSentiment.regime || 'N/A'}
                              </div>
                              <div className="text-gray-600 text-sm">
                                {overallSentiment.score}/100
                              </div>
                              {overallSentiment.change && (
                                <div className="text-gray-500 text-xs">
                                  {overallSentiment.change > 0 ? '+' : ''}{overallSentiment.change} pts weekly
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <Activity className="w-5 h-5 text-gray-400" />
                            <div className="text-gray-400 text-sm">Data unavailable</div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Key Event - FROM NEWS WEB SEARCH */}
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                      <h4 className="text-gray-500 text-sm font-medium mb-2">Key Event Today</h4>
                      <div>
                        <div className="text-gray-900 font-semibold text-sm">
                          {dailyRecap.key_event?.title || 'No key event today'}
                        </div>
                        <div className="text-gray-600 text-xs mt-1">
                          {dailyRecap.key_event?.description || 'Check back for updates'}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-center py-8">Loading market brief...</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Market Overview - 1/3 Width on Right */}
        <div className="lg:col-span-1 h-full">
          <MarketOverview />
        </div>
      </div>

      {/* TradingView Widgets - Full Width */}
      <div className="space-y-6">
        <Card className="bg-white border-gray-200 shadow-card hover:shadow-card-hover transition-shadow duration-300">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-navy text-lg">Market Data</CardTitle>
              <div className="flex items-center space-x-2">
                <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-200">
                  <Activity className="w-3 h-3 mr-1" />
                  Live
                </Badge>
                <Badge variant="outline" className="text-xs text-gray-600">
                  Powered by TradingView
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <TradingViewMarketData height="650px" />
          </CardContent>
        </Card>

        {/* ETF Heatmap Widget */}
        <Card className="bg-white border-gray-200 shadow-card hover:shadow-card-hover transition-shadow duration-300">
          <CardHeader>
            <CardTitle className="text-navy text-lg">ETF Heatmap</CardTitle>
          </CardHeader>
          <CardContent>
            <TradingViewETFHeatmap height="600px" />
          </CardContent>
        </Card>
      </div>

      {/* AI Analysis Modal */}
      <AIAnalysisModal 
        isOpen={isAnalysisModalOpen}
        onClose={() => setIsAnalysisModalOpen(false)}
        dailyRecap={dailyRecap}
      />

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setIsVideoModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-sm w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-orange-50">
              <h3 className="text-lg font-semibold text-gray-900">Welcome to Era - Introduction</h3>
              <button 
                onClick={() => setIsVideoModalOpen(false)}
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="aspect-[9/16] bg-gray-900">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/-V5Iq1QpdNw"
                title="Welcome to Era"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      )}
      
      <InlineDisclaimer section="ai_commentary" />
      {/* Investment Disclaimer */}
      <Disclaimer />
    </div>
  );
};

export default HomePage;